import {type NonEmptyArray, headOnNonEmpty, mkNonEmptySingleton} from '@freckle/non-empty'
import {
  type ParserT,
  Parser,
  nonEmptyArray,
  nullable,
  record,
  string,
  number,
  typeOf,
  firstOf,
  pure
} from '@freckle/parser'

import {validationErrorsParser} from '@frontrow/console/src/entities/models/validation-errors'

export type ApiErrorT = {
  name: string
  message: string
  details:
    | {
        [x: string]: unknown
      }
    | undefined
    | null
}

// ApiError might be created from multiple returned errors which need to be flattened
// into a single error to be thrown
/** @internal */
export const flattenApiErrors = (apiErrors: NonEmptyArray<ApiErrorT>): ApiErrorT => {
  const firstError = headOnNonEmpty(apiErrors)
  if (apiErrors.length === 1) {
    return firstError
  } else {
    return {
      // Since there may be multiple types of errors, flatten to a generic name for grouping
      name: 'MultipleApiErrors',
      message: `${apiErrors.length} errors returned`,
      details: {
        allErrors: apiErrors // Preserve the original error object
      }
    }
  }
}

const apiErrorParser: ParserT<ApiErrorT> = record({
  name: string(),
  message: string(),
  details: nullable(typeOf('object'))
})

const fallbackErrorParser: ParserT<NonEmptyArray<ApiErrorT>> = Parser.map(
  record({
    responseJSON: record({
      errors: nonEmptyArray(apiErrorParser)
    })
  }) as ParserT<{
    responseJSON: {
      errors: NonEmptyArray<ApiErrorT>
    }
  }>,
  'projectErrors',
  (result: {
    responseJSON: {
      errors: NonEmptyArray<ApiErrorT>
    }
  }) => result.responseJSON.errors
)

// Errors raised by the openapi-fetch path arrive as a `FetchError`
// (src/open-api/middleware/error.ts), which carries the decoded response body
// on `response.json` where jQuery's jqXHR carries it on `responseJSON`. Without
// this branch the same server response yields a worse message on the newer
// transport than on the older one.
const fetchErrorsParser: ParserT<NonEmptyArray<ApiErrorT>> = Parser.map(
  record({
    response: record({
      json: firstOf(
        Parser.map(
          record({
            validationErrors: nonEmptyArray(apiErrorParser)
          }) as ParserT<{
            validationErrors: NonEmptyArray<ApiErrorT>
          }>,
          'projectFetchValidationErrors',
          (body: {validationErrors: NonEmptyArray<ApiErrorT>}) => body.validationErrors
        ),
        Parser.map(
          record({
            errors: nonEmptyArray(apiErrorParser)
          }) as ParserT<{
            errors: NonEmptyArray<ApiErrorT>
          }>,
          'projectFetchErrors',
          (body: {errors: NonEmptyArray<ApiErrorT>}) => body.errors
        )
      )
    })
  }) as ParserT<{
    response: {
      json: NonEmptyArray<ApiErrorT>
    }
  }>,
  'projectFetchErrorBody',
  (result: {
    response: {
      json: NonEmptyArray<ApiErrorT>
    }
  }) => result.response.json
)

// A `FetchError` whose body carried no errors of its own — an HTML error page
// from a proxy in front of the API, say. Reports what `xhrErrorParser` reports
// for the jQuery path, minus the `readyState` that only XHR has. Keyed on the
// nested `response.response` so a jqXHR still takes `xhrErrorParser`'s branch.
const fetchErrorStatusParser: ParserT<NonEmptyArray<ApiErrorT>> = Parser.map(
  record({
    status: number(),
    response: record({
      response: typeOf('object')
    })
  }) as ParserT<{
    status: number
    response: {
      response: unknown
    }
  }>,
  'projectFetchErrorStatus',
  ({status}) =>
    mkNonEmptySingleton({
      name: 'ApiError',
      message: `Server responded with ${status}`,
      details: {
        status
      }
    })
)

const xhrErrorParser: ParserT<NonEmptyArray<ApiErrorT>> = Parser.map(
  record({
    status: number(),
    readyState: number()
  }) as ParserT<{
    status: number
    readyState: number
  }>,
  'projectStatusError',
  ({status, readyState}) =>
    mkNonEmptySingleton({
      name: 'ApiError',
      message: `Server responded with ${status}`,
      details: {
        status,
        readyState
      }
    })
)

/** @internal */
export const mkApiErrorsParser = (
  fallbackMessage: string = 'Unknown error'
): ParserT<NonEmptyArray<ApiErrorT>> =>
  firstOf(
    validationErrorsParser as ParserT<NonEmptyArray<ApiErrorT>>,
    fallbackErrorParser,
    fetchErrorsParser,
    xhrErrorParser,
    fetchErrorStatusParser,
    pure(
      mkNonEmptySingleton({
        name: 'ApiError',
        message: fallbackMessage,
        details: null
      })
    )
  )

export const responseApiErrors = (
  response: unknown,
  fallbackMessage?: string
): NonEmptyArray<ApiErrorT> => {
  const apiErrorsParser = mkApiErrorsParser(fallbackMessage)
  return Parser.run(response, apiErrorsParser)
}

export const firstResponseApiError = (response: unknown, fallbackMessage?: string): ApiErrorT =>
  headOnNonEmpty(responseApiErrors(response, fallbackMessage))
